import React, { useCallback, useEffect, useRef, useState } from "react";
import { useWindowSize } from "@infrastructure/Hooks/useWindowSize";
import {
    CommentTextOutlinedInput,
    ErrorContainer,
    FormContainerStack,
    FormImageInputStack,
    FormInputStack,
    ImagesFormControlLabel,
    InputsContainer,
    LoadingStack,
    ModalActionsSubmitStack,
    OptionsContainer,
    OptionsIconButton,
    SubmitButton,
    TabsStyled,
} from "./CommentFormModal.styles";
import {
    isDeviceOutputMobileOrMobileApp,
    isMobileAppDeviceOutput,
} from "../../../../Domain/DeviceOutputState";
import { ButtonProps } from "@mui/material";
import {
    useInterruptableAction,
    UserLoginActionState,
    useUserStore,
} from "@infrastructure/Persistence/UserState";
import {
    RESPONSE_CODE_INAPPROPRIATE_TEXT,
    RESPONSE_CODE_LIMIT_EXCEEDED,
    RESPONSE_CODE_NOT_AUTHORIZED,
    RESPONSE_CODE_PROFILE_SUSPENDED,
    RESPONSE_CODE_RESOURCE_NOT_FOUND,
    RESPONSE_CODE_RESOURCE_SUSPENDED,
} from "@infrastructure/BackendClientCodes";
import { useAutosizeTextArea } from "../../AutosizeTextArea/useAutosizeTextArea";
import { ImageSolidIcon } from "@icons/ImageSolidIcon";
import { LinkSolidIcon } from "@icons/LinkSolidIcon";
import { CommentFormModalType, FORM_TABS } from "./CommentFormModal.types";
import { CommunityCircularProgress } from "@shared/styles";
import { formatLink, isValidUrl } from "@infrastructure/Helpers/UrlHelper";
import { ModalConstructor } from "./ModalConstructor";
import {
    ErrorState,
    useErrorStore,
} from "../ServiceModals/GeneralErrorsModal/GlobalState";
import { ImagesPreview } from "../../Post/PostForm/ImagesPreview";
import { ServiceModalInnerContainerStyles } from "../ServiceModals/ServiceModal.styles";
import {
    CommentFormModeEnum,
    useCommentModalState,
} from "./CommentFormModalOpenState";
import { useUploadPostCommentImages } from "./useUploadPostCommentImages";
import {
    addComment,
    editComment,
} from "@infrastructure/Services/PostCommentsService";
import { CommentImageType } from "../../Comment/CommentTypes";
import { CommentCreationErrorModal } from "../ServiceModals/CommentCreationErrorModal";
import { MediaFilesUploadInput } from "../../MediaFilesUploadInput/MediaFilesUploadInput";
import { useMediaFilesUploadInputStore } from "../../MediaFilesUploadInput/MediaFilesUploadInput.store";
import { useGlobalConfigs } from "@infrastructure/Hooks/useGlobalConfigs";
import { UploadImagesInput } from "../../Post/PostForm/PostFormModal.types";
import { LinkoutField } from "../../Post/PostForm/LinkoutField";
import { SendIcon } from "./Icon/SendIcon";
import { InvalidMediaUploadModal } from "../ServiceModals/InvalidMediaUploadModal/InvalidMediaUploadModal";
import {
    RESET_API_CACHE_REASONS,
    resetIgnoreApiCacheTime,
} from "@infrastructure/Services/ApiUtility";
import { useInputFocusedStore } from "./useInputFocusedStore";
import { PostCommentsInfo } from "@infrastructure/Services/PostCommentsService.types";
import { useUserCommentedPostsStore } from "@infrastructure/Hooks/UserInteractions/useUserCommentedPostsStore";
import { useDeferredErrorText } from "@infrastructure/Hooks/useDeferredErrorText";
import { CommentInputReply } from "../../Comment/CommentInputReply";
import { useLinkout } from "@infrastructure/Hooks/useLinkout";
import useDetectKeyboardOpen from "use-detect-keyboard-open";

export function CommentFormModal({
    postId,
    commentFormSubmissionCallback,
    replyComment,
    clearReplyComment,
    editedComment,
    clearEditedComment,
    sourceType,
}: CommentFormModalType) {
    const abortController = useRef<AbortController>(new AbortController());
    const { isOpen, setIsOpen, mode, setMode } = useCommentModalState();
    const isKeyboardOpen = useDetectKeyboardOpen();

    const [tabIndex, setTabIndex] = useState(FORM_TABS.DEFAULT);
    const [commentText, setCommentText] = useState("");

    const [formActive, setFormActive] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [showLinkoutUrlInput, setShowLinkoutUrlInput] = useState(
        mode === CommentFormModeEnum.LINKOUT,
    );
    const [isCommentCreationErrorModalOpen, setCommentCreationErrorModalOpen] =
        useState(false);
    const inputFileRef = useRef<HTMLInputElement>(null);
    const { getConfigs } = useGlobalConfigs();
    //(createCommentAddLinkEnabled) config is not available for Tippspiel but we want to turn off the feature for now
    const createCommentAddLinkEnabled =
        getConfigs()?.createCommentAddLinkEnabled;

    const { images, setImages, isUploading } = useMediaFilesUploadInputStore();
    const { linkoutUrl, setLinkoutUrl, clearLinkout } = useLinkout(
        commentText,
        editedComment?.linkoutUrl || "",
    );

    const triggerFileUploadInput = useCallback(() => {
        inputFileRef.current?.click();
    }, []);

    useEffect(() => {
        if (!editedComment) {
            return;
        }
        setCommentText(editedComment.content || "");
        setLinkoutUrl(editedComment.linkoutUrl || "");
        setShowLinkoutUrlInput(editedComment.linkoutUrl?.length > 0 ?? false);
        setImages(
            editedComment.images?.map((image: CommentImageType) => ({
                id: image.id,
                imageUrl: image.url,
                imageFile: undefined,
                delete: false,
                isUploading: false,
                isInappropriate: false,
                failed: false,
                uploaded: false,
            })) || [],
        );
    }, [editedComment]);

    useEffect(() => {
        if (isOpen) {
            setCommentText(editedComment?.content ?? "");
            setLinkoutUrl(editedComment?.linkoutUrl ?? "");
        }
    }, [isOpen]);

    const submitButtonProps: ButtonProps = {
        variant: "contained",
        disableElevation: true,
        disabled: !formActive || isSubmitting,
        size: "large",
        onClick: (e) => {
            commentSubmitHandler(e);
        },
    };

    const handleClose = () => {
        setImages([]);
        setIsOpen(false);
        setTabIndex(FORM_TABS.DEFAULT);
        setShowLinkoutUrlInput(false);
        clearEditedComment();
        clearReplyComment();
        abortController.current.abort();
        abortController.current = new AbortController();
    };
    const { addToCommentedPosts } = useUserCommentedPostsStore();
    const submitCallback = () => {
        if (linkoutUrl.length > 0 && !isValidUrl(linkoutUrl)) {
            setLinkoutErrorText("Ungültiger Link");
            setIsSubmitting(false);
            return;
        }
        const imagesToSave = images.filter(
            (image) => image.id || (image.key && image.bucket),
        );

        const commentData = {
            text: commentText,
            linkoutUrl: formatLink(linkoutUrl),
            referencedComment: replyComment ? replyComment.id : undefined,
            images: imagesToSave,
            sourceType,
        };

        const commentUpdate = editedComment?.id
            ? editComment(editedComment.id.toString(), commentData)
            : addComment(postId.toString(), commentData);

        commentUpdate
            .then((update: PostCommentsInfo) => {
                resetIgnoreApiCacheTime(
                    RESET_API_CACHE_REASONS.COMMENT_CREATE_OR_UPDATE,
                );
                setIsSubmitting(false);
                handleClose();
                commentFormSubmissionCallback(update);
                addToCommentedPosts(postId);
            })
            .catch((err) => {
                switch (err.response?.status) {
                    case RESPONSE_CODE_NOT_AUTHORIZED:
                        break;
                    case RESPONSE_CODE_INAPPROPRIATE_TEXT:
                        setInappropriateTextError(true);
                        setIsSubmitting(false);
                        break;
                    case RESPONSE_CODE_RESOURCE_SUSPENDED:
                    case RESPONSE_CODE_RESOURCE_NOT_FOUND:
                        setErrorState(ErrorState.COMMENT_SUSPENDED);
                        setIsSubmitting(false);
                        handleClose();
                        break;
                    case RESPONSE_CODE_PROFILE_SUSPENDED:
                        setErrorState(ErrorState.USER_INACTIVE_OR_SUSPENDED);
                        setIsSubmitting(false);
                        handleClose();
                        break;
                    case RESPONSE_CODE_LIMIT_EXCEEDED:
                        setErrorState(ErrorState.LIMIT_EXCEEDED_ERROR);
                        setIsSubmitting(false);
                        handleClose();
                        break;
                    default:
                        if (err.final === undefined) {
                            setCommentCreationErrorModalOpen(true);
                            setIsSubmitting(false);
                        }
                }
            });
    };

    const cancelCallback = () => {
        setIsSubmitting(false);
        handleClose();
    };

    useInterruptableAction(isSubmitting, submitCallback, cancelCallback);

    const { setErrorState } = useErrorStore();

    function commentSubmitHandler(event: React.MouseEvent<HTMLElement>) {
        event.preventDefault();
        if (!isSubmitting) {
            setIsSubmitting(true);
            setUserAuthContextState(UserLoginActionState.CREATE_COMMENT);
            submitCallback();
        }
    }

    function handleCommentTextChange(event: any) {
        const textVal: string = event?.target.value;
        setCommentText(textVal);
    }

    const handleTabChange = (event, newTabIndex) => {
        setTabIndex(newTabIndex);
    };

    const isMobileApp = isMobileAppDeviceOutput();
    const inputRef = useRef<HTMLTextAreaElement>(null);
    useAutosizeTextArea(inputRef?.current, commentText, 94, undefined, 36);
    const { setInputFocused } = useInputFocusedStore();

    const [inappropriateTextError, setInappropriateTextError] = useState(false);
    const [inappropriateImageError, setInappropriateImageError] =
        useState(false);
    const [errorText, setErrorText] = useState("");
    const [linkoutErrorText, setLinkoutErrorText] = useState("");
    const { setUserAuthContextState } = useUserStore();

    const isDeviceMobile = isDeviceOutputMobileOrMobileApp();
    const { isMobileScreenSizeFlag } = useWindowSize();

    const inputLinkoutRef = useRef<HTMLInputElement>();

    useEffect(() => {
        if (!isOpen || mode === CommentFormModeEnum.DEFAULT) {
            return;
        }

        setShowLinkoutUrlInput(
            mode === CommentFormModeEnum.LINKOUT || linkoutUrl.length > 0,
        );

        if (mode && mode === CommentFormModeEnum.TEXT && inputRef.current) {
            inputRef.current.focus();
        } else if (
            showLinkoutUrlInput &&
            inputLinkoutRef.current &&
            commentText.length === 0
        ) {
            inputLinkoutRef.current?.focus();
        }
    }, [
        showLinkoutUrlInput,
        commentText.length,
        isOpen,
        linkoutUrl.length,
        mode,
        inputRef.current,
        inputLinkoutRef.current,
    ]);

    const uploadImagesParam: UploadImagesInput = {
        images,
        setImages,
        setFormActive,
        setIsUploading: () => {},
        open: isOpen,
        abortController: abortController.current,
    };

    const [showErrorText, setShowErrorText] = useState(false);
    useDeferredErrorText({
        active: !inappropriateTextError,
        textInput: commentText,
        showErrorCallback: () => {
            setShowErrorText(true);
        },
        hideErrorCallback: () => {
            setShowErrorText(false);
        },
    });

    const {
        resetUpload,
        handleOnRemoveImage,
        handleDragImage,
        inappropriateContentDetected,
        failedUploadsDetected,
    } = useUploadPostCommentImages(uploadImagesParam);

    useEffect(() => {
        setInappropriateTextError(false);
        setInappropriateImageError(false);
        setLinkoutErrorText("");

        let allImagesAreLoaded = true;
        for (const image of images) {
            if (image.isUploading) {
                allImagesAreLoaded = false;
                break;
            }
        }

        const isLinkValid = showLinkoutUrlInput ? isValidUrl(linkoutUrl) : true;

        const formIsValid =
            allImagesAreLoaded &&
            (images.filter((image) => !image.delete).length > 0 ||
                commentText.trim().length > 1);
        setFormActive(formIsValid && isLinkValid);
        setErrorText("");
        if (commentText.trim().length === 1 && !formIsValid) {
            setErrorText("Mind. 2 Zeichen erforderlich");
        }
    }, [images, commentText, linkoutUrl, showLinkoutUrlInput]);

    useEffect(() => {
        if (inappropriateTextError) {
            setShowErrorText(true);
            setErrorText(
                "Dein Textbeitrag wurde aufgrund unserer Richtlinien leider abgelehnt.",
            );
        } else if (inappropriateImageError) {
            setShowErrorText(true);
            setErrorText(
                "Dein Bildbeitrag wurde aufgrund unserer Richtlinien leider abgelehnt.",
            );
        } else if (failedUploadsDetected) {
            setShowErrorText(true);
            setErrorText(
                "Bilder Upload fehlgeschlagen. Bitte versuche es erneut.",
            );
        } else {
            setShowErrorText(false);
            setErrorText("");
        }
    }, [
        inappropriateTextError,
        inappropriateImageError,
        failedUploadsDetected,
    ]);

    const onBlur = () => {
        setInputFocused(false);
    };

    return (
        <ModalConstructor
            onClose={() => {
                if (!isSubmitting) {
                    handleClose();
                }
            }}
            open={true}
            modalContent={
                <TabsStyled value={tabIndex} onChange={handleTabChange}>
                    <MediaFilesUploadInput limit={20} ref={inputFileRef} />
                    {!isSubmitting && tabIndex === FORM_TABS.DEFAULT && (
                        <FormContainerStack>
                            {replyComment?.user?.screenName && (
                                <CommentInputReply
                                    replyComment={replyComment}
                                />
                            )}
                            <InputsContainer>
                                <CommentTextOutlinedInput
                                    className={
                                        errorText.length && showErrorText
                                            ? "_error"
                                            : ""
                                    }
                                    placeholder="Kommentieren…"
                                    name="commentText"
                                    value={commentText}
                                    ref={inputRef}
                                    sx={{
                                        border:
                                            commentText.length > 0
                                                ? "1px solid var(--community-color-dark-gray)"
                                                : "1px solid var(--community-color-transparent-black-05)",
                                    }}
                                    rows={1}
                                    onTouchEnd={(e) => {
                                        e.stopPropagation();
                                    }}
                                    onInput={handleCommentTextChange}
                                    onFocus={(e) => {
                                        setMode(CommentFormModeEnum.TEXT);
                                        e.currentTarget.setSelectionRange(
                                            e.currentTarget.value.length,
                                            e.currentTarget.value.length,
                                        );
                                        setInputFocused(true);
                                    }}
                                    onBlur={onBlur}
                                />

                                {images.filter((item) => !item.delete)
                                    .length === 0 && (
                                    <ImagesFormControlLabel
                                        control={
                                            <FormImageInputStack
                                                onClick={triggerFileUploadInput}
                                            >
                                                <OptionsIconButton
                                                    sx={{
                                                        "&:hover": {
                                                            backgroundColor:
                                                                "var(--community-color-chapeau-violet)",
                                                        },
                                                    }}
                                                >
                                                    <ImageSolidIcon />
                                                </OptionsIconButton>
                                            </FormImageInputStack>
                                        }
                                        label=""
                                    />
                                )}
                                {createCommentAddLinkEnabled &&
                                    !showLinkoutUrlInput && (
                                        <FormInputStack
                                            onPointerUp={(e) => {
                                                e.preventDefault();
                                                setMode(
                                                    CommentFormModeEnum.LINKOUT,
                                                );
                                                setInputFocused(false);
                                            }}
                                            onTouchEnd={(e) => {
                                                e.stopPropagation();
                                            }}
                                        >
                                            <OptionsIconButton
                                                sx={{
                                                    "&:hover": {
                                                        backgroundColor:
                                                            isDeviceMobile
                                                                ? "var(--community-color-transparent-black-05)"
                                                                : "var(--community-color-chapeau-violet)",
                                                    },
                                                }}
                                                onPointerUp={() => {
                                                    setMode(
                                                        CommentFormModeEnum.LINKOUT,
                                                    );
                                                }}
                                                onTouchEnd={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                }}
                                            >
                                                <LinkSolidIcon
                                                    color={
                                                        "var(--community-color-primary-text)"
                                                    }
                                                />
                                            </OptionsIconButton>
                                        </FormInputStack>
                                    )}

                                {formActive &&
                                    !isSubmitting &&
                                    !isUploading && (
                                        <OptionsContainer>
                                            <ModalActionsSubmitStack>
                                                <SubmitButton
                                                    {...submitButtonProps}
                                                >
                                                    <SendIcon />
                                                </SubmitButton>
                                            </ModalActionsSubmitStack>
                                        </OptionsContainer>
                                    )}
                            </InputsContainer>

                            {errorText && showErrorText && (
                                <ErrorContainer>{errorText}</ErrorContainer>
                            )}
                            <ImagesPreview
                                triggerInput={triggerFileUploadInput}
                                handleOnRemoveImage={handleOnRemoveImage}
                                images={images}
                                isHidden={
                                    images.filter((image) => !image.delete)
                                        .length === 0
                                }
                                handleDragImage={handleDragImage}
                                handleImageUploadRetry={resetUpload}
                                inappropriateContentDetected={
                                    inappropriateContentDetected
                                }
                                failedUploadsDetected={failedUploadsDetected}
                                scrollable={true}
                            />
                            {createCommentAddLinkEnabled &&
                                showLinkoutUrlInput && (
                                    <LinkoutField
                                        linkoutRef={inputLinkoutRef}
                                        mode={mode}
                                        errorText={errorText}
                                        linkoutUrl={linkoutUrl}
                                        setLinkoutUrl={setLinkoutUrl}
                                        onClear={() => {
                                            clearLinkout();
                                            setMode(CommentFormModeEnum.TEXT);
                                        }}
                                        setMode={setMode}
                                    />
                                )}
                            {linkoutErrorText && showLinkoutUrlInput && (
                                <ErrorContainer>
                                    {linkoutErrorText}
                                </ErrorContainer>
                            )}
                        </FormContainerStack>
                    )}
                    {isSubmitting && (
                        <LoadingStack>
                            <CommunityCircularProgress />
                        </LoadingStack>
                    )}
                    <InvalidMediaUploadModal />
                    <CommentCreationErrorModal
                        open={isCommentCreationErrorModalOpen}
                        setOpen={setCommentCreationErrorModalOpen}
                    />
                </TabsStyled>
            }
            isBorderRadiusVisible={!isMobileScreenSizeFlag}
            modalInnerContainerStyles={{
                paddingBottom:
                    isMobileApp && !isKeyboardOpen
                        ? "var(--safe-area-inset-bottom)"
                        : 0,
                ...ServiceModalInnerContainerStyles,
                "@media (min-width: 640px)": {
                    borderBottomRightRadius: "12px",
                    borderBottomLeftRadius: "12px",
                },
            }}
        />
    );
}
